// Field rules for the market APIs, POST /markets and PATCH /markets/{id}.
// Both take multipart/form-data. Checked against the live service on
// 2026-09-16: symbol must match /^[a-z0-9]{1,16}$/, decimals is an integer
// from 0 to 18 and is required when creating, and dailyWithdrawLimit must be
// a non-negative decimal string with at most 18 fractional digits.
import { asTrimmedString, decimalProblem, integerProblem, trimDecimal } from "config/decimals";

export const SYMBOL_PATTERN = /^[a-z0-9]{1,16}$/;
export const MAX_SYMBOL_LENGTH = 16;
export const MAX_DECIMALS = 18;
// GET /markets rejects a limit above this.
export const MARKETS_MAX_PAGE_LIMIT = 20;

// Re-exported so the market pages have one import for their field helpers.
export { trimDecimal };

export const MARKET_FIELDS = {
  name: { label: "Market Name", placeholder: "Market Name" },
  symbol: { label: "Market Symbol", placeholder: "e.g. usdt", hint: "Lowercase letters and numbers only, up to 16 characters." },
  decimals: { label: "Decimals", placeholder: "e.g. 8", hint: "Whole number from 0 to 18." },
  dailyWithdrawLimit: { label: "Daily Withdraw Limit", placeholder: "e.g. 1000000", hint: "Up to 18 decimal places." },
};

// Keeps what the user types inside what the API accepts.
export const normalizeSymbolInput = (value) =>
  asTrimmedString(value).toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, MAX_SYMBOL_LENGTH);

// Error keys match the ids already used by the market forms.
export function validateMarketForm({ name, symbol, decimals, dailyWithdrawLimit, icon, requireIcon = true }) {
  const errors = {};

  if (requireIcon && !icon) errors.marketIcon = "Market icon is required";
  if (!asTrimmedString(name)) errors.marketName = "Market name is required";

  const symbolText = asTrimmedString(symbol);
  if (!symbolText) {
    errors.marketBase = "Market symbol is required";
  } else if (!SYMBOL_PATTERN.test(symbolText)) {
    errors.marketBase = `Use lowercase letters and numbers only, up to ${MAX_SYMBOL_LENGTH} characters`;
  }

  const decimalsText = asTrimmedString(decimals);
  if (!decimalsText) {
    errors.decimals = "Decimals is required";
  } else {
    const problem = integerProblem(decimalsText, { label: MARKET_FIELDS.decimals.label, min: 0, max: MAX_DECIMALS });
    if (problem) errors.decimals = problem;
  }

  const limitText = asTrimmedString(dailyWithdrawLimit);
  if (!limitText) {
    errors.dailyWithdrawLimit = "Daily withdraw limit is required";
  } else {
    // The service accepts zero here, so only the format is checked.
    const problem = decimalProblem(limitText, MARKET_FIELDS.dailyWithdrawLimit.label, { allowZero: true });
    if (problem) errors.dailyWithdrawLimit = problem;
  }

  return errors;
}

// Call only after validateMarketForm returns no errors. On edit the icon is
// optional: leave it out to keep the market's current image.
export function buildMarketFormData({ name, symbol, decimals, dailyWithdrawLimit, isEssential, isListed, isAvailable, icon }) {
  const formData = new FormData();
  if (icon) formData.append("icon", icon);
  formData.append("name", asTrimmedString(name));
  formData.append("symbol", asTrimmedString(symbol).toLowerCase());
  formData.append("decimals", String(parseInt(decimals, 10)));
  formData.append("dailyWithdrawLimit", asTrimmedString(dailyWithdrawLimit));
  formData.append("isEssential", isEssential ? "true" : "false");
  formData.append("isListed", isListed ? "true" : "false");
  formData.append("isAvailable", isAvailable ? "true" : "false");
  return formData;
}
